import Panel from './panel';
import {Marker, Popup} from 'mapbox-gl';
import configs from '../configs';
import {loadSafieDevices, loadIchimillDevices, loadKoujiLocations} from '../loader';
import ichimillIcon from '../../public/images/ichimill_kage.png';

/**
 * 日付をYYYY/MM/DD hh:mm形式にフォーマットする
 * @param {string|Date} dateInput - 日付文字列またはDateオブジェクト
 * @returns {string} フォーマットされた日付文字列
 */
function formatUpdatedAt(dateInput) {
    if (!dateInput) {
        return '不明';
    }
    
    try {
        const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
        
        if (isNaN(date.getTime())) {
            return '不明';
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}/${month}/${day} ${hours}:${minutes}`;
    } catch (error) {
        console.warn('日付のフォーマットに失敗しました:', dateInput, error);
        return '不明';
    }
}

const MARKER_Z_INDEX_BASE = 1000;
const MARKER_Z_INDEX_SCALE = 10000;

function applyMarkerZIndex(markerElement, lngLat) {
    if (!markerElement || !lngLat) {
        return;
    }

    const lat = Array.isArray(lngLat) ? lngLat[1] : lngLat.lat;
    if (!Number.isFinite(lat)) {
        return;
    }

    // Smaller latitude (further south) should be in front.
    const zIndex = MARKER_Z_INDEX_BASE + Math.round((90 - lat) * MARKER_Z_INDEX_SCALE);
    markerElement.style.zIndex = `${zIndex}`;
}

function normalizeLngLat(coord) {
    if (!Array.isArray(coord) || coord.length < 2) {
        return null;
    }

    let [lng, lat] = coord.map(Number);

    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
        return null;
    }

    // [lat, lng] の誤配置を補正
    if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) {
        [lng, lat] = [lat, lng];
    }

    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        return null;
    }

    return [lng, lat];
}

export default class extends Panel {

    constructor(options) {
        super(Object.assign({
            className: 'location-panel',
            modal: true
        }, options));
    }

    addTo(map) {
        const me = this,
            locationItems = me._options.locationItems || [];

        super.addTo(map)
            .setTitle(map.dict && map.dict['locations'] || '位置情報')
            .setHTML(locationItems.map(item => [
                `<div id="${item.id}-location" class="location-row">`,
                '<div class="location-icon"></div>',
                `<div>${item.name}</div>`,
                '</div>'
            ].join('')).join(''));

        for (const item of locationItems) {
            const rowElement = me._container.querySelector(`#${item.id}-location`),
                iconElement = rowElement && rowElement.querySelector('.location-icon');

            if (!rowElement || !iconElement) {
                continue;
            }

            const classList = iconElement.classList,
                toggleLocation = () => {
                    if (item.enabled) {
                        classList.remove('location-icon-enabled');
                        item.enabled = false;
                        try {
                            me._hideLocationMarkers(map, item.id);
                        } catch (error) {
                            console.error(`位置情報の非表示に失敗しました: ${item.id}`, error);
                        }
                    } else {
                        classList.add('location-icon-enabled');
                        item.enabled = true;
                        try {
                            me._showLocationMarkers(map, item.id);
                        } catch (error) {
                            console.error(`位置情報の表示に失敗しました: ${item.id}`, error);
                        }
                    }

                    if (typeof map.refreshSearchTargets === 'function') {
                        map.refreshSearchTargets();
                    }
                };

            // アイコンスタイルの設定
            Object.assign(iconElement.style, {
                backgroundColor: item.color || '#ff6b6b',
                borderRadius: '50%',
                width: '16px',
                height: '16px',
                display: 'inline-block',
                marginRight: '8px'
            });

            iconElement.addEventListener('click', toggleLocation);

            if (item.enabled) {
                classList.add('location-icon-enabled');
                // 初期化時に有効な項目のマーカーを表示
                try {
                    me._showLocationMarkers(map, item.id);
                } catch (error) {
                    console.error(`初期表示のマーカー描画に失敗しました: ${item.id}`, error);
                }
            }
        }

        if (typeof map.refreshSearchTargets === 'function') {
            map.refreshSearchTargets();
        }

        me._bindPopupAutoClose(map);

        return me;
    }

    _getLocationItem(locationId) {
        const locationItems = this._options.locationItems || [];

        return locationItems.find(item => item.id === locationId) || null;
    }

    _setLocationIconEnabled(locationId, enabled) {
        const rowElement = this._container && this._container.querySelector(`#${locationId}-location`),
            iconElement = rowElement && rowElement.querySelector('.location-icon');

        if (!iconElement) {
            return;
        }
        iconElement.classList.toggle('location-icon-enabled', Boolean(enabled));
    }

    setLocationEnabled(map, locationId, enabled) {
        const item = this._getLocationItem(locationId),
            nextEnabled = Boolean(enabled);

        if (!item) {
            return false;
        }

        item.enabled = nextEnabled;
        this._setLocationIconEnabled(locationId, nextEnabled);

        try {
            if (nextEnabled) {
                this._showLocationMarkers(map, locationId);
            } else {
                this._hideLocationMarkers(map, locationId);
            }
        } catch (error) {
            console.error(`位置情報の表示状態更新に失敗しました: ${locationId}`, error);
        }

        if (typeof map.refreshSearchTargets === 'function') {
            map.refreshSearchTargets();
        }

        return true;
    }

    _isLocationEnabled(locationId) {
        const item = this._getLocationItem(locationId);

        return Boolean(item && item.enabled);
    }

    _renderSafieMarkers(map, devices = []) {
        this._hideLocationMarkers(map, 'safie-cameras');

        if (!this._isLocationEnabled('safie-cameras')) {
            return;
        }

        devices.forEach(device => {
            this._createSafieMarker(map, device);
        });
    }

    _renderIchimillMarkers(map, devices = []) {
        this._hideLocationMarkers(map, 'ichimill-cameras');

        if (!this._isLocationEnabled('ichimill-cameras')) {
            return;
        }

        devices.forEach(device => {
            this._createIchimillMarker(map, device);
        });
    }

    _renderKoujiMarkers(map, locationId, status, color, markerKey, locations = []) {
        // 定期同期でマーカーを作り直しても、ユーザーが開いている工事詳細
        // （および選択連動する3Dモデル）は維持する。
        this._hideLocationMarkers(map, locationId, {preserveKoujiPopup: true});

        if (!this._isLocationEnabled(locationId)) {
            return;
        }

        locations
            .filter(location => location.status === status)
            .forEach(location => {
                this._createKoujiMarker(map, location, color, markerKey);
            });
    }

    _syncLocationMarkers(map, locationId) {
        if (locationId === 'safie-cameras') {
            this._renderSafieMarkers(map, map.activeSafieDevices || []);
        } else if (locationId === 'ichimill-cameras') {
            this._renderIchimillMarkers(map, map.activeIchimillDevices || []);
        } else if (locationId === 'kouji-under-construction') {
            this._renderKoujiMarkers(
                map,
                'kouji-under-construction',
                'UNDER_CONSTRUCTION',
                '#ff3b30',
                '_koujiUnderConstructionMarkers',
                map.activeKoujiLocations || []
            );
        } else if (locationId === 'kouji-complete') {
            this._renderKoujiMarkers(
                map,
                'kouji-complete',
                'COMPLETE',
                '#1e90ff',
                '_koujiCompleteMarkers',
                map.activeKoujiLocations || []
            );
        }
    }

    _showLocationMarkers(map, locationId) {
        console.log(`Show location markers for: ${locationId}`);

        if (!this._isLocationEnabled(locationId)) {
            console.log(`Skip showing markers because location is disabled: ${locationId}`);
            this._hideLocationMarkers(map, locationId);
            return;
        }
        
        if (locationId === 'safie-cameras') {
            // 実際のSafieデバイス情報を取得して表示
            if (map.activeSafieDevices && map.activeSafieDevices.length > 0) {
                // 既存のSafieデバイスデータを使用
                this._renderSafieMarkers(map, map.activeSafieDevices);
            } else {
                // Safieデバイス情報がまだ取得されていない場合の処理
                console.log('Safieデバイスデータの読み込みを待機中...');
                
                // 少し待ってから再試行（初期化時の非同期読み込みを待つ）
                setTimeout(() => {
                    if (!this._isLocationEnabled(locationId)) {
                        return;
                    }

                    if (map.activeSafieDevices && map.activeSafieDevices.length > 0) {
                        // 待機後にデータが利用可能になった場合
                        console.log('待機後にSafieデバイスデータが利用可能になりました');
                        this._renderSafieMarkers(map, map.activeSafieDevices);
                    } else {
                        // まだデータがない場合は手動取得
                        console.log('手動でSafieデバイス情報を取得します');
                        loadSafieDevices().then((devicesData) => {
                            const SafieDevice = map.getConstructor('SafieDevice');

                            if (!this._isLocationEnabled(locationId)) {
                                return;
                            }

                            this._renderSafieMarkers(map, devicesData.map((deviceData) => {
                                return new SafieDevice(deviceData);
                            }));
                        }).catch((error) => {
                            console.error('Safieデバイス情報の取得に失敗しました:', error);
                            // 取得に失敗した場合は何も表示しない
                        });
                    }
                }, 2000); // 2秒待機
            }
        } else if (locationId === 'ichimill-cameras') {
            // 実際のIchimillデバイス情報を取得して表示
            if (map.activeIchimillDevices && map.activeIchimillDevices.length > 0) {
                // 既存のIchimillデバイスデータを使用
                this._renderIchimillMarkers(map, map.activeIchimillDevices);
            } else {
                // Ichimillデバイス情報がまだ取得されていない場合の処理
                console.log('Ichimillデバイスデータの読み込みを待機中...');
                
                // 少し待ってから再試行（初期化時の非同期読み込みを待つ）
                setTimeout(() => {
                    if (!this._isLocationEnabled(locationId)) {
                        return;
                    }

                    if (map.activeIchimillDevices && map.activeIchimillDevices.length > 0) {
                        // 待機後にデータが利用可能になった場合
                        console.log('待機後にIchimillデバイスデータが利用可能になりました');
                        this._renderIchimillMarkers(map, map.activeIchimillDevices);
                    } else {
                        // まだデータがない場合は手動取得
                        console.log('手動でIchimillデバイス情報を取得します');
                        loadIchimillDevices().then((devicesData) => {
                            const IchimillDevice = map.getConstructor('IchimillDevice');

                            if (!this._isLocationEnabled(locationId)) {
                                return;
                            }

                            this._renderIchimillMarkers(map, devicesData.map((deviceData) => {
                                return new IchimillDevice(deviceData);
                            }));
                        }).catch((error) => {
                            console.error('Ichimillデバイス情報の取得に失敗しました:', error);
                            // 取得に失敗した場合は何も表示しない
                        });
                    }
                }, 2000); // 2秒待機
            }
        } else if (locationId === 'kouji-under-construction') {
            this._showKoujiMarkers(
                map,
                'kouji-under-construction',
                'UNDER_CONSTRUCTION',
                '#ff3b30',
                '_koujiUnderConstructionMarkers'
            );
        } else if (locationId === 'kouji-complete') {
            this._showKoujiMarkers(
                map,
                'kouji-complete',
                'COMPLETE',
                '#1e90ff',
                '_koujiCompleteMarkers'
            );
        }
    }

    _bindPopupAutoClose(map) {
        if (map._locationPanelPopupCloseBound) {
            return;
        }
        map._locationPanelPopupCloseBound = true;

        const closeIfUnrelated = (event) => {
            const originalEvent = event && event.originalEvent ? event.originalEvent : event;
            if (this._isPopupRelatedClick(originalEvent)) {
                return;
            }
            this._closeAllPopups(map);
        };

        map._locationPanelPopupCloseHandler = closeIfUnrelated;
        document.addEventListener('click', closeIfUnrelated);

        if (typeof map.refreshMap === 'function' && !map._locationPanelRefreshWrapped) {
            map._locationPanelRefreshWrapped = true;
            map._locationPanelRefreshMapOriginal = map.refreshMap.bind(map);
            map.refreshMap = (...args) => {
                this._closeAllPopups(map);
                return map._locationPanelRefreshMapOriginal(...args);
            };
        }
    }

    _isPopupRelatedClick(event) {
        if (!event || !event.target) {
            return false;
        }

        const target = event.target.closest ? event.target : event.target.parentElement;
        if (!target || !target.closest) {
            return false;
        }

        return Boolean(target.closest(
            '.popup-object, .mapboxgl-popup, .safie-location-marker, .ichimill-location-marker, .kouji-location-marker, .mapboxgl-marker'
        ));
    }

    _closeAllPopups(map) {
        if (map._currentSafiePopup) {
            map._currentSafiePopup.remove();
            map._currentSafiePopup = null;
            map._currentSafieDeviceId = null;
        }

        if (map._currentIchimillPopup) {
            map._currentIchimillPopup.remove();
            map._currentIchimillPopup = null;
            map._currentIchimillDeviceId = null;
        }

        if (map._currentKoujiPopup) {
            map._currentKoujiPopup.remove();
        }
    }

    _createSafieMarker(map, safieDevice) {
        const markerId = `safie-marker-${safieDevice.id}`;
        
        // カメラアイコンをSVGで作成
        const svgIcon = `
            <svg width="62.5" height="62.5" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;">
                <!-- 灰色の背景 - カメラサイズに合わせて調整 -->
                <rect x="70" y="170" width="660" height="480" rx="20" ry="20" style="fill:#808080;opacity:0.8;"/>
                <g transform="matrix(1,0,0,1,-235,-809)">
                    <g transform="matrix(2.02015,0,0,2.02017,-51.8034,252.584)">
                        <rect x="142.327" y="275.741" width="396.011" height="396.011" style="fill:none;"/>
                        <g transform="matrix(1,0,0,1,9.5568e-13,-1.7024)">
                            <g transform="matrix(0.495014,0,0,0.495007,25.6434,-125.031)">
                                <path d="M704.313,1012.32L733.646,1106.99C730.088,1107.88 669.55,1107.58 629.646,1107.28C589.743,1107.58 529.205,1107.88 525.646,1106.99L554.98,1012.32L704.313,1012.32Z" style="fill:url(#_Linear1-${safieDevice.id});"/>
                            </g>
                            <g transform="matrix(0.495014,0,0,0.495007,25.6434,-125.031)">
                                <rect x="770.971" y="1038.35" width="94.675" height="44" style="fill:url(#_Linear2-${safieDevice.id});"/>
                            </g>
                            <g transform="matrix(0.495014,0,0,0.429599,10.5496,-77.5453)">
                                <rect x="387.783" y="1110.79" width="556.855" height="407.759" style="fill:url(#_Linear3-${safieDevice.id});"/>
                            </g>
                            <g transform="matrix(0.495014,0,0,0.296136,10.5496,113.276)">
                                <rect x="387.783" y="1110.79" width="556.855" height="407.759" style="fill:url(#_Linear4-${safieDevice.id});"/>
                            </g>
                            <g transform="matrix(0.365612,0,0,0.365607,-68.6578,1.42644)">
                                <circle cx="823.091" cy="1155.35" r="26.778" style="fill:url(#_Linear5-${safieDevice.id});"/>
                            </g>
                            <g transform="matrix(0.240361,0,0,0.240357,-708.01,195.362)">
                                <circle cx="4361.54" cy="1220.52" r="308.919" style="fill:url(#_Linear6-${safieDevice.id});"/>
                            </g>
                            <g transform="matrix(0.195483,0,0,0.195481,-512.276,250.135)">
                                <circle cx="4361.54" cy="1220.52" r="308.919" style="fill:url(#_Linear7-${safieDevice.id});"/>
                            </g>
                        </g>
                    </g>
                </g>
                <defs>
                    <linearGradient id="_Linear1-${safieDevice.id}" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="matrix(138.667,0,0,95.2593,525.646,1029.98)"><stop offset="0" style="stop-color:rgb(189,193,195);stop-opacity:1"/><stop offset="1" style="stop-color:rgb(118,122,133);stop-opacity:1"/></linearGradient>
                    <linearGradient id="_Linear2-${safieDevice.id}" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="matrix(94.6751,0,0,44,770.971,1060.35)"><stop offset="0" style="stop-color:rgb(81,88,95);stop-opacity:1"/><stop offset="1" style="stop-color:rgb(33,39,46);stop-opacity:1"/></linearGradient>
                    <linearGradient id="_Linear3-${safieDevice.id}" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="matrix(802.447,0,0,1095.86,387.783,1314.67)"><stop offset="0" style="stop-color:rgb(222,221,218);stop-opacity:1"/><stop offset="1" style="stop-color:rgb(213,213,209);stop-opacity:1"/></linearGradient>
                    <linearGradient id="_Linear4-${safieDevice.id}" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="matrix(1041.88,0,0,4256.25,148.351,1314.67)"><stop offset="0" style="stop-color:rgb(213,209,167);stop-opacity:1"/><stop offset="1" style="stop-color:rgb(148,94,15);stop-opacity:1"/></linearGradient>
                    <linearGradient id="_Linear5-${safieDevice.id}" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="matrix(149.478,0,0,149.478,700.391,1155.35)"><stop offset="0" style="stop-color:rgb(252,204,161);stop-opacity:1"/><stop offset="1" style="stop-color:rgb(223,100,78);stop-opacity:1"/></linearGradient>
                    <linearGradient id="_Linear6-${safieDevice.id}" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="matrix(3.60471e-12,617.839,-617.839,3.60471e-12,4361.54,911.598)"><stop offset="0" style="stop-color:rgb(33,39,46);stop-opacity:1"/><stop offset="1" style="stop-color:rgb(81,88,95);stop-opacity:1"/></linearGradient>
                    <linearGradient id="_Linear7-${safieDevice.id}" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="matrix(3.60471e-12,617.839,-617.839,3.60471e-12,4361.54,911.598)"><stop offset="0" style="stop-color:rgb(89,86,172);stop-opacity:1"/><stop offset="1" style="stop-color:rgb(122,192,232);stop-opacity:1"/></linearGradient>
                </defs>
            </svg>
        `;
        
        // マーカー要素を作成
        const markerElement = document.createElement('div');
        markerElement.id = markerId;
        markerElement.className = 'safie-location-marker';
        markerElement.innerHTML = `
            <div class="camera-icon" style="
                width: 62.5px;
                height: 62.5px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
            ">
                ${svgIcon}
            </div>
        `;
        
        const lngLat = [safieDevice.coord[0], safieDevice.coord[1]];
        applyMarkerZIndex(markerElement, lngLat);

        // Mapbox GL JSのマーカーとして追加
        const marker = new Marker({element: markerElement})
            .setLngLat(lngLat)
            .addTo(map.getMapboxMap());
        
        // マーカーの管理用配列に追加
        if (!map._safieMarkers) {
            map._safieMarkers = [];
        }
        map._safieMarkers.push(marker);
        
        // Safie専用のポップアップ表示
        markerElement.addEventListener('click', () => {
            this._showSafiePopup(map, safieDevice, marker.getLngLat());
        });
    }

    _showSafiePopup(map, safieDevice, lngLat) {
        // 同じデバイスのポップアップが表示されている場合はトグル動作（閉じる）
        if (map._currentSafiePopup && map._currentSafieDeviceId === safieDevice.id) {
            map._currentSafiePopup.remove();
            map._currentSafiePopup = null;
            map._currentSafieDeviceId = null;
            return;
        }

        this._closeAllPopups(map);

        // Safie専用ポップアップを作成
        const popup = new Popup({
            className: 'popup-object safie-popup',
            closeButton: true,
            closeOnClick: false,
            maxWidth: '300px',
            offset: [0, -40]
        });

        // ポップアップ内容を作成
        const imageDataUrl = safieDevice.getImageDataUrl();
        const imageHtml = imageDataUrl ?
            `<div class="safie-snapshot">
                <img src="${imageDataUrl}"
                     alt="Latest Snapshot"
                     style="max-width: 200px; max-height: 150px; border-radius: 4px; margin-top: 8px;"
                     onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
                <div style="display: none; color: #666; margin-top: 8px;">画像の読み込みに失敗しました</div>
            </div>` :
            '<div class="safie-no-image" style="color: #666; margin-top: 8px;">画像データなし</div>';

        const eyelinkUrl = window.EYELINK_URL || 'https://eyelink.deepsmart.kumagaigumi.co.jp';
        const popupHtml = `
            <div class="safie-device-popup">
                <strong style="color: white;">${safieDevice.name}</strong>
                <div class="safie-device-info">
                    <div>KDS Eyelink：<a href="${eyelinkUrl}/#/minitokyo?deviceId=${safieDevice.id}" target="_blank" rel="noopener noreferrer" style="color: #0066FF; text-decoration: underline; pointer-events: auto; z-index: 9999; position: relative; font-size: 12px;">${eyelinkUrl}</a></div>
                    <div>カメラID: ${safieDevice.id}</div>
                    <div>更新日時: ${formatUpdatedAt(safieDevice.updatedAt)}</div>
                    <div style="font-size: 12px; color: #666;">
                        位置: ${safieDevice.coord[1].toFixed(6)}, ${safieDevice.coord[0].toFixed(6)}
                    </div>
                </div>
                ${imageHtml}
            </div>
        `;

        popup
            .setLngLat(lngLat)
            .setHTML(popupHtml)
            .addTo(map.getMapboxMap());

        // 現在のポップアップを管理用変数に保存
        map._currentSafiePopup = popup;
        map._currentSafieDeviceId = safieDevice.id;

        // ポップアップが閉じられた時の処理
        popup.on('close', () => {
            map._currentSafiePopup = null;
            map._currentSafieDeviceId = null;
        });
    }

    _createIchimillMarker(map, ichimillDevice) {
        const markerId = `ichimill-marker-${ichimillDevice.id}`;

        // マーカー要素を作成
        const markerElement = document.createElement('div');
        markerElement.id = markerId;
        markerElement.className = 'ichimill-location-marker';
        markerElement.innerHTML = `
            <div class="camera-icon" style="
                width: 62.5px;
                height: 62.5px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
            ">
                <img
                    src="${ichimillIcon}"
                    alt="建設機械(開発中)"
                    style="
                        width: 44px;
                        height: 44px;
                        object-fit: contain;
                    "
                >
            </div>
        `;
        
        const lngLat = [ichimillDevice.coord[0], ichimillDevice.coord[1]];
        applyMarkerZIndex(markerElement, lngLat);

        // Mapbox GL JSのマーカーとして追加
        const marker = new Marker({element: markerElement})
            .setLngLat(lngLat)
            .addTo(map.getMapboxMap());
        
        // マーカーの管理用配列に追加
        if (!map._ichimillMarkers) {
            map._ichimillMarkers = [];
        }
        map._ichimillMarkers.push(marker);
        
        // Ichimill専用のポップアップ表示
        markerElement.addEventListener('click', () => {
            this._showIchimillPopup(map, ichimillDevice, marker.getLngLat());
        });
    }

    _showIchimillPopup(map, ichimillDevice, lngLat) {
        // トグル動作
        if (map._currentIchimillPopup && map._currentIchimillDeviceId === ichimillDevice.id) {
            map._currentIchimillPopup.remove();
            map._currentIchimillPopup = null;
            map._currentIchimillDeviceId = null;
            return;
        }

        this._closeAllPopups(map);

        const popup = new Popup({
            className: 'popup-object ichimill-popup',
            closeButton: true,
            closeOnClick: false,
            maxWidth: '300px',
            offset: [0, -40]
        });

        // ポップアップ内容を作成（EyelinkのURLと画像は不要）
        const popupHtml = `
            <div class="ichimill-device-popup">
                <strong style="color: white;">${ichimillDevice.name}</strong>
                <div class="ichimill-device-info">
                    <div>デバイスID: ${ichimillDevice.id}</div>
                    <div>更新日時: ${formatUpdatedAt(ichimillDevice.updatedAt)}</div>
                    <div style="font-size: 12px; color: #666;">
                        位置: ${ichimillDevice.coord[1].toFixed(6)}, ${ichimillDevice.coord[0].toFixed(6)}
                    </div>
                </div>
            </div>
        `;

        popup.setLngLat(lngLat).setHTML(popupHtml).addTo(map.getMapboxMap());

        // 現在のポップアップを管理
        map._currentIchimillPopup = popup;
        map._currentIchimillDeviceId = ichimillDevice.id;

        popup.on('close', () => {
            map._currentIchimillPopup = null;
            map._currentIchimillDeviceId = null;
        });
    }

    _showKoujiMarkers(map, locationId, status, color, markerKey) {
        const showMarkers = (locations) => {
            this._renderKoujiMarkers(map, locationId, status, color, markerKey, locations);
        };

        if (map.activeKoujiLocations && map.activeKoujiLocations.length > 0) {
            showMarkers(map.activeKoujiLocations);
        } else {
            console.log('工事情報データの読み込みを待機中...');

            setTimeout(() => {
                if (!this._isLocationEnabled(locationId)) {
                    return;
                }

                if (map.activeKoujiLocations && map.activeKoujiLocations.length > 0) {
                    console.log('待機後に工事情報データが利用可能になりました');
                    showMarkers(map.activeKoujiLocations);
                } else {
                    console.log('手動で工事情報を取得します');
                    loadKoujiLocations().then((locationsData) => {
                        const KoujiLocation = map.getConstructor('KoujiLocation');

                        if (!this._isLocationEnabled(locationId)) {
                            return;
                        }

                        showMarkers(locationsData.map((locationData) => {
                            return KoujiLocation ? new KoujiLocation(locationData) : locationData;
                        }));
                    }).catch((error) => {
                        console.error('工事情報の取得に失敗しました:', error);
                    });
                }
            }, 2000); // 2秒待機
        }
    }

    _createKoujiMarker(map, koujiLocation, color, markerKey) {
        if (!koujiLocation || !koujiLocation.coord) {
            return;
        }

        const markerId = `kouji-marker-${koujiLocation.id}-${koujiLocation.status}`;
        const markerElement = document.createElement('div');
        markerElement.id = markerId;
        markerElement.className = 'kouji-location-marker';
        markerElement.title = koujiLocation.name || '工事情報';

        const svgIcon = `
            <svg width="31.25" height="31.25" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M32 3C21.1 3 12.2 11.9 12.2 22.8C12.2 38.1 32 61 32 61C32 61 51.8 38.1 51.8 22.8C51.8 11.9 42.9 3 32 3Z" fill="${color}" stroke="#ffffff" stroke-width="3" stroke-linejoin="round" />
                <circle cx="32" cy="22.8" r="7.2" fill="#ffffff" />
            </svg>
        `;

        markerElement.innerHTML = `
            <div class="kouji-pin-icon">
                ${svgIcon}
            </div>
        `;

        const lngLat = normalizeLngLat(koujiLocation.coord);
        if (!lngLat) {
            console.warn('工事情報マーカーをスキップしました: 無効な座標', koujiLocation.id, koujiLocation.coord);
            return;
        }
        applyMarkerZIndex(markerElement, lngLat);

        const marker = new Marker({element: markerElement, anchor: 'bottom'})
            .setLngLat(lngLat)
            .addTo(map.getMapboxMap());

        if (!map[markerKey]) {
            map[markerKey] = [];
        }
        map[markerKey].push(marker);

        markerElement.addEventListener('click', () => {
            this._showKoujiPopup(map, koujiLocation, marker.getLngLat());
        });
    }

    _showKoujiPopup(map, koujiLocation, lngLat) {
        if (map._currentKoujiPopup && map._currentKoujiId === koujiLocation.id) {
            map._currentKoujiPopup.remove();
            return;
        }

        this._closeAllPopups(map);

        const koujiBaseUrl = configs.koujiSiteUrl || '';
        const koujiNumber = koujiLocation.koujiBangou || '';
        const koujiUrl = koujiBaseUrl && koujiNumber
            ? `${koujiBaseUrl.replace(/\/$/, '')}/${encodeURIComponent(koujiNumber)}`
            : '';
        const koujiLinkHtml = koujiUrl
            ? `<a href="${koujiUrl}" target="_blank" rel="noopener noreferrer" style="color: #0066FF; text-decoration: underline; pointer-events: auto; z-index: 9999; position: relative;">${koujiUrl}</a>`
            : '<span>未設定</span>';
        const popupHtml = `
            <div class="kouji-location-popup" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 13px; line-height: 1.4;">
                <strong style="color: white;">${koujiLocation.koujiName || koujiLocation.name || '工事情報'}</strong>
                <div style="margin-top: 6px;">
                    <div>工事情報サイト：${koujiLinkHtml}</div>
                </div>
            </div>
        `;

        const popup = new Popup({
            className: 'popup-object kouji-popup',
            closeButton: true,
            closeOnClick: false,
            maxWidth: '300px',
            offset: [0, -20]
        });

        popup.setLngLat(lngLat).setHTML(popupHtml).addTo(map.getMapboxMap());

        map._currentKoujiPopup = popup;
        map._currentKoujiId = koujiLocation.id;
        map._currentKoujiStatus = koujiLocation.status;
        map._currentKoujiBangou = koujiLocation.koujiBangou;

        map.fire({
            type: 'koujiselection',
            koujiBangou: koujiLocation.koujiBangou,
            koujiId: koujiLocation.id
        });

        popup.on('close', () => {
            if (map._currentKoujiPopup !== popup) {
                return;
            }
            map._currentKoujiPopup = null;
            map._currentKoujiId = null;
            map._currentKoujiStatus = null;
            map._currentKoujiBangou = null;
            map.fire({
                type: 'koujideselection',
                koujiBangou: koujiLocation.koujiBangou,
                koujiId: koujiLocation.id
            });
        });
    }

    _hideLocationMarkers(map, locationId, options = {}) {
        // Safie位置情報の非表示処理
        console.log(`Hide location markers for: ${locationId}`);
        
        if (locationId === 'safie-cameras' && map._safieMarkers) {
            // 既存のポップアップを削除
            if (map._currentSafiePopup) {
                map._currentSafiePopup.remove();
                map._currentSafiePopup = null;
                map._currentSafieDeviceId = null;
            }
            
            // 既存のマーカーを削除
            map._safieMarkers.forEach(marker => {
                marker.remove();
            });
            map._safieMarkers = [];
        } else if (locationId === 'ichimill-cameras' && map._ichimillMarkers) {
            // 既存のポップアップを削除
            if (map._currentIchimillPopup) {
                map._currentIchimillPopup.remove();
                map._currentIchimillPopup = null;
                map._currentIchimillDeviceId = null;
            }
            
            // 既存のマーカーを削除
            map._ichimillMarkers.forEach(marker => {
                marker.remove();
            });
            map._ichimillMarkers = [];
        } else if (locationId === 'kouji-under-construction' && map._koujiUnderConstructionMarkers) {
            if (map._currentKoujiPopup && !options.preserveKoujiPopup) {
                map._currentKoujiPopup.remove();
            }

            map._koujiUnderConstructionMarkers.forEach(marker => {
                marker.remove();
            });
            map._koujiUnderConstructionMarkers = [];
        } else if (locationId === 'kouji-complete' && map._koujiCompleteMarkers) {
            if (map._currentKoujiPopup && !options.preserveKoujiPopup) {
                map._currentKoujiPopup.remove();
            }

            map._koujiCompleteMarkers.forEach(marker => {
                marker.remove();
            });
            map._koujiCompleteMarkers = [];
        }
    }

}
